import { GlobalStore } from '@roc/feature-app-core';
import { action, flow, makeObservable, observable } from 'mobx';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';
import { GalleryService } from '../services/galleryService';
import { GalleryPhoto } from '../types/galleryTypes';
import PlaceHolderImage from '../../assets/Placeholder.svg';

export class GalleryPhotosStore {
  private globalStore: GlobalStore;
  private galleryService: GalleryService;
  /** Every object URL we create, so we can revoke them and avoid blob leaks. */
  private objectUrls: Set<string> = new Set();
  photos: GalleryPhoto[] = [];
  photoSelected: GalleryPhoto | null = null;
  cachedImages: Record<string, string> = {};
  isLoading: boolean = false;

  constructor(globalStore: GlobalStore, galleryService: GalleryService) {
    this.globalStore = globalStore;
    this.galleryService = galleryService;

    makeObservable(this, {
      photos: observable,
      photoSelected: observable,
      isLoading: observable,
      loadPhotos: flow,
      selectPhoto: flow,
      fetchPhoto: flow,
      setPhotoPreferredRotation: action,
      reset: action,
    });

    this.reset();
  }

  *loadPhotos(userId: number, folderId: string) {
    this.isLoading = true;
    this.photoSelected = null;
    // Free blob URLs from the previously loaded folder before loading a new one.
    this.revokeObjectUrls();
    this.cachedImages = {};
    try {
      const response = yield this.galleryService.getPhotos(userId, folderId);
      if (response?.data?.status === 'OK') {
        const photos: GalleryPhoto[] = response?.data?.data?.photos || [];
        this.photos = photos;
        yield this.fetchThumbnails(userId, photos);
      } else {
        throw new Error('There was an error while retrieving the gallery photos.');
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while retrieving the gallery photos.',
      });
    } finally {
      this.isLoading = false;
    }
  }

  async fetchThumbnails(userId: number, photos: GalleryPhoto[]) {
    await Promise.all(
      photos.map(async photo => {
        try {
          const thumbnailBlob = await this.galleryService.downloadThumbnail(
            userId,
            photo.uuid
          );
          const url = this.trackObjectUrl(thumbnailBlob.data as any);
          photo.image = url;
          photo.thumbnailImage = url;
        } catch (err) {
          console.error(`Failed to load gallery photo ${photo.uuid}`, err);
          photo.image = PlaceHolderImage;
          photo.thumbnailImage = PlaceHolderImage;
        }
      })
    );
    this.photos = [...photos];
  }

  *selectPhoto(userId: number | undefined, photo: GalleryPhoto) {
    this.photoSelected = photo;
    if (userId && photo) {
      yield this.fetchPhoto(userId, photo);
    }
  }

  *fetchPhoto(userId: number, photo: GalleryPhoto) {
    try {
      if (!this.cachedImages[photo.uuid]) {
        const photoBlob = yield this.galleryService.downloadPhoto(userId, photo.uuid);
        const blobURL = this.trackObjectUrl(photoBlob.data as any);
        photo.image = blobURL;
        this.cachedImages[photo.uuid] = blobURL;
        this.photos = [...this.photos];
      } else {
        photo.image = this.cachedImages[photo.uuid];
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  setPhotoPreferredRotation(photoUuid: string, rotationDegrees: number) {
    const photo = this.photos.find(p => p.uuid === photoUuid);
    if (photo) {
      photo.preferredRotation = rotationDegrees;
      this.photos = [...this.photos];
    }
  }

  reset() {
    this.revokeObjectUrls();
    this.photos = [];
    this.photoSelected = null;
    this.cachedImages = {};
    this.isLoading = false;
  }

  private trackObjectUrl(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);
    return url;
  }

  private revokeObjectUrls() {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls.clear();
  }
}
